import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  COMPANY_ADDRESS, COMPANY_ADDRESS_LINES, COMPANY_NAME, LEGAL_NAME, SUPPORT_EMAIL,
} from "@/constants/company";

const UPDATED = "September 2026";
const SECTION = {
  "/refunds": "refunds",
  "/cancellations": "cancellations",
  "/returns": "returns",
};

export default function Policies() {
  const { pathname } = useLocation();
  useEffect(() => {
    const id = SECTION[pathname];
    if (id) document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [pathname]);

  return (
    <div className="max-w-[860px] mx-auto px-6 lg:px-10 pt-28 pb-24">
      <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-2">Legal</div>
      <h1 className="font-serif text-4xl tracking-tight mb-2">Returns, refunds &amp; cancellations</h1>
      <p className="text-xs text-aura-muted mb-8">Last updated: {UPDATED}</p>

      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        {LEGAL_NAME} (“{LEGAL_NAME}”, “we”, “us”) operates {COMPANY_NAME} from{" "}
        {COMPANY_ADDRESS}. {COMPANY_NAME} is part of {LEGAL_NAME}. This page is our return policy, refund and dispute policy,
        cancellation policy, and legal / export restrictions. It applies to purchases and
        digital services paid on shoptosa.com.{" "}
        <Link to="/terms" className="link-underline">Terms of Service</Link> still apply.
      </p>

      <h2 id="who-you-buy-from" className="font-serif text-2xl mt-10 mb-3">Who you buy from</h2>
      <ul className="text-sm text-aura-muted leading-relaxed mb-4 list-disc pl-5 space-y-1">
        <li>
          <b>Partner / affiliate listings</b> checkout on the retailer’s website. That retailer’s
          return, refund, shipping, and cancellation policies apply. We may earn a commission
          at no extra cost to you.
        </li>
        <li>
          <b>Shoptosa checkout</b> (bag paid with Stripe or Cashfree) is a sale fulfilled by
          {COMPANY_NAME} or an independent seller on our platform. This page covers those orders.
        </li>
        <li>
          <b>AI try-on credits</b> and <b>seller plans</b> are digital services sold by {COMPANY_NAME}.
        </li>
      </ul>

      <h2 id="returns" className="font-serif text-2xl mt-10 mb-3">Return policy (physical goods)</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        For Shoptosa checkout orders of physical goods, you may request a return within{" "}
        <b>30 days of delivery</b> if the item is unused, in original condition, and in its
        original packaging (tags attached where applicable).
      </p>
      <p className="text-sm text-aura-muted leading-relaxed mb-2">How to start a return:</p>
      <ol className="text-sm text-aura-muted leading-relaxed mb-4 list-decimal pl-5 space-y-1">
        <li>Email <a href={`mailto:${SUPPORT_EMAIL}`} className="link-underline">{SUPPORT_EMAIL}</a> with your order ID, email, and reason.</li>
        <li>We confirm eligibility and send a return address or seller return instructions.</li>
        <li>Ship the item back within 14 days of that confirmation. Keep the tracking number.</li>
        <li>After we (or the seller) inspect the return, we issue the refund.</li>
      </ol>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        Return shipping is paid by the buyer unless the item arrived damaged, defective, or
        not as described — then we cover return shipping. Opened software, perishable grocery,
        personal-care items that have been used, and custom-made pieces are not returnable
        unless they arrived damaged or not as described.
      </p>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        Partner-site purchases must be returned through that retailer using their process.
        If you need help finding their policy, email {SUPPORT_EMAIL} with the product link
        and order confirmation.
      </p>

      <h2 id="refunds" className="font-serif text-2xl mt-10 mb-3">Refund and dispute policy</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        Approved returns are refunded to the original payment method within{" "}
        <b>5–10 business days</b> after inspection. Shipping charges are refunded only when
        we made an error or the item was defective.
      </p>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        <b>AI try-on credits.</b> Unused credit packs may be refunded within 14 days of purchase
        if no paid try-on from that pack has been generated. Credits already used to generate
        a look are a delivered digital service and are not refundable. The three complimentary
        tries are free and have no cash value.
      </p>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        <b>Disputes.</b> Email {SUPPORT_EMAIL} first so we can fix a missing, damaged, or
        incorrect order. If we cannot resolve it, you may file a dispute with your card issuer
        or payment provider (including Stripe). Chargebacks opened without contacting us may
        delay a replacement. We respond to payment-network inquiries with order and shipping records.
      </p>

      <h2 id="cancellations" className="font-serif text-2xl mt-10 mb-3">Cancellation policy</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        <b>Shoptosa bag orders.</b> Email {SUPPORT_EMAIL} as soon as possible. We cancel and
        refund in full if the order has not shipped. After shipment, use the return process above.
      </p>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        <b>Seller plans.</b> Cancel anytime by emailing {SUPPORT_EMAIL} from the account email.
        Access continues through the end of the paid month or year. We do not prorate unused
        days unless required by law. Cancel at least 24 hours before renewal to avoid the next charge.
      </p>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        <b>AI credit packs.</b> Cancel an unpaid checkout at any time. After payment, unused packs
        follow the refund rule above; there is no recurring subscription for try-on credits.
      </p>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        Partner-site orders are cancelled with that retailer.
      </p>

      <h2 id="restrictions" className="font-serif text-2xl mt-10 mb-3">Legal and export restrictions</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        You must be at least 16 (or the age of majority where you live) to use {COMPANY_NAME}.
        We do not sell, and sellers may not list, illegal goods, stolen property, counterfeit
        items, weapons restricted by US law, or anything that violates US export, sanctions,
        or consumer-safety rules.
      </p>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        We do not knowingly ship Shoptosa checkout orders to destinations prohibited by US
        sanctions (including OFAC comprehensively sanctioned countries). Partner retailers
        set their own ship-to countries. Cross-border buyers are responsible for duties,
        taxes, and local import rules.
      </p>

      <h2 id="promotions" className="font-serif text-2xl mt-10 mb-3">Promotions</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        If we offer a discount, credit, or promotion, the terms shown with that offer control
        (who is eligible, expiry, and whether it stacks). Unless stated otherwise, one promotion
        per order, not redeemable for cash, and we may end a promotion at any time.
      </p>

      <h2 className="font-serif text-2xl mt-10 mb-3">Contact</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-1">{LEGAL_NAME}</p>
      <p className="text-sm text-aura-muted leading-relaxed mb-1">{COMPANY_NAME} is part of {LEGAL_NAME}.</p>
      {COMPANY_ADDRESS_LINES.map((line) => (
        <p key={line} className="text-sm text-aura-muted leading-relaxed mb-0">{line}</p>
      ))}
      <p className="text-sm text-aura-muted leading-relaxed mt-3">
        Customer service:{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="link-underline">{SUPPORT_EMAIL}</a>
        {" · "}
        <Link to="/contact" className="link-underline">Contact page</Link>
      </p>
    </div>
  );
}
